
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { FileText, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const DisputeResponse = () => {
  const { disputeId } = useParams<{ disputeId: string }>();
  const { toast } = useToast();
  const [response, setResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!response.trim()) {
      toast({
        title: "Response Required",
        description: "Please enter a response before submitting.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const responseData = await fetch('/api/dispute_respond', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          disputeId,
          response: response.trim(),
          timestamp: new Date().toISOString(),
        }),
      });

      if (responseData.ok) {
        toast({
          title: "Response Submitted",
          description: `Your response to dispute ${disputeId} has been recorded.`,
        });
        setResponse('');
      } else {
        throw new Error('Failed to submit response');
      }
    } catch (error) {
      console.error('Error submitting response:', error);
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your response. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <Card className="p-6">
          <div className="flex items-center space-x-2 mb-6">
            <FileText className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl font-bold">Dispute Response</h1>
          </div>

          <div className="mb-6">
            <Label className="text-sm font-medium text-muted-foreground">Dispute ID</Label>
            <div className="flex items-center space-x-2 mt-1">
              <Badge variant="outline" className="font-mono">
                {disputeId}
              </Badge>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="response">Your Response *</Label>
              <Textarea
                id="response"
                placeholder="Enter your response to this dispute..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                className="mt-2 min-h-32"
                disabled={isSubmitting}
              />
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              <Send className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Submitting...' : 'Submit Response'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default DisputeResponse;
